<div class="assay-documents-container p-4 flex flex-column gap-4">

  <!-- Header Card / Banner -->
  <div class="surface-card border-round-xl p-4 shadow-1 border-1 border-200">
    <div class="flex flex-column md:flex-row justify-content-between align-items-start md:align-items-center gap-3">
      <div>
        <div class="flex align-items-center gap-2 mb-1">
          <i class="pi pi-file-pdf text-xl text-primary"></i>
          <span class="text-xl font-bold text-900">Documentos de Calidad Asociados</span>
        </div>
        <p class="text-500 m-0 line-height-3 text-sm">
          Gestione los documentos de calidad vinculados a este ensayo y sus determinaciones. El sistema consulta y provee siempre la <strong>versión vigente</strong> de cada documento.
        </p>
      </div>

      <div class="flex align-items-center gap-2 flex-shrink-0">
        <p-button
          label="Asociar Documento"
          icon="pi pi-plus"
          [rounded]="true"
          severity="primary"
          (onClick)="openLinkDialog()">
        </p-button>
      </div>
    </div>

    <!-- Filter / Scope Selector -->
    <div class="flex flex-column sm:flex-row align-items-start sm:align-items-center gap-3 mt-4 pt-3 border-top-1 border-200">
      <span class="text-sm font-semibold text-700">Filtrar por ámbito:</span>
      <p-select
        [options]="scopeOptions()"
        [ngModel]="selectedScope()"
        (ngModelChange)="selectedScope.set($event)"
        optionLabel="label"
        optionValue="value"
        placeholder="Seleccionar ámbito"
        class="w-full sm:w-20rem">
      </p-select>

      <span class="text-xs text-500 ml-auto">
        Mostrando {{ filteredDocuments().length }} documento(s)
      </span>
    </div>
  </div>

  <!-- Loading State -->
  <div *ngIf="isLoading()" class="flex flex-column align-items-center justify-content-center py-6">
    <p-progressSpinner strokeWidth="4" [style]="{ width: '40px', height: '40px' }"></p-progressSpinner>
    <span class="text-500 text-sm mt-3">Cargando documentos asociados...</span>
  </div>

  <ng-container *ngIf="!isLoading()">

    <!-- SECTION 1: DOCUMENTOS PRINCIPALES -->
    <div class="surface-card border-round-xl p-4 shadow-1 border-1 border-200">
      <div class="flex justify-content-between align-items-center mb-3">
        <div class="flex align-items-center gap-2">
          <div class="badge-icon-circle bg-blue-50 text-blue-600">
            <i class="pi pi-book"></i>
          </div>
          <div>
            <h3 class="text-lg font-bold text-900 m-0">Documentos Principales</h3>
            <small class="text-500">Procedimientos operativos estándar y métodos analíticos del ensayo.</small>
          </div>
        </div>
        <p-button
          label="+ Agregar Principal"
          [text]="true"
          size="small"
          severity="info"
          (onClick)="openLinkDialog('PRINCIPAL')">
        </p-button>
      </div>

      <div *ngIf="!principalDocuments().length" class="empty-state-box p-4 border-round-lg border-1 border-dashed border-300 text-center">
        <i class="pi pi-file text-3xl text-400 mb-2"></i>
        <p class="text-600 font-medium m-0">No hay documentos principales vinculados</p>
        <small class="text-500">Haga clic en "+ Agregar Principal" para asociar un documento principal.</small>
      </div>

      <p-table
        *ngIf="principalDocuments().length"
        [value]="principalDocuments()"
        styleClass="p-datatable-sm p-datatable-striped"
        responsiveLayout="scroll">
        <ng-template pTemplate="header">
          <tr>
            <th style="width: 15%">Código</th>
            <th style="width: 25%">Nombre Documento</th>
            <th style="width: 15%">Tipo</th>
            <th style="width: 15%">Ámbito</th>
            <th style="width: 12%">Versión Vigente</th>
            <th style="width: 10%">Notas</th>
            <th style="width: 8%" class="text-center">Acciones</th>
          </tr>
        </ng-template>
        <ng-template pTemplate="body" let-doc>
          <tr>
            <td>
              <span class="font-bold text-primary">{{ doc.qualityDocument?.code || '—' }}</span>
            </td>
            <td>
              <span class="font-semibold text-800">{{ doc.qualityDocument?.name }}</span>
            </td>
            <td>
              <p-tag [value]="doc.qualityDocument?.docType?.value || 'Doc'" severity="secondary"></p-tag>
            </td>
            <td>
              <span class="text-sm text-700">
                <i class="pi" [ngClass]="doc.assayAnaliteId ? 'pi-filter text-indigo-500' : 'pi-building text-blue-500'"></i>
                {{ getScopeLabel(doc) }}
              </span>
            </td>
            <td>
              <ng-container *ngIf="getValidVersion(doc.qualityDocument) as validVer">
                <div class="flex align-items-center gap-2">
                  <p-tag [value]="'v' + validVer.versionNumber" severity="success"></p-tag>
                  <button
                    *ngIf="validVer.file"
                    pButton
                    type="button"
                    icon="pi pi-download"
                    class="p-button-rounded p-button-text p-button-sm text-primary"
                    pTooltip="Descargar versión vigente (PDF)"
                    tooltipPosition="top"
                    (click)="downloadDocumentFile(doc.qualityDocument)">
                  </button>
                </div>
              </ng-container>
              <span *ngIf="!getValidVersion(doc.qualityDocument)" class="text-xs text-400">Sin versión vigente</span>
            </td>
            <td>
              <span class="text-sm text-600 font-italic" [pTooltip]="doc.notes" tooltipPosition="top">
                {{ doc.notes ? (doc.notes | slice:0:30) + (doc.notes.length > 30 ? '...' : '') : '—' }}
              </span>
            </td>
            <td class="text-center">
              <button
                pButton
                type="button"
                icon="pi pi-trash"
                class="p-button-rounded p-button-text p-button-danger p-button-sm"
                pTooltip="Desvincular documento"
                tooltipPosition="top"
                (click)="confirmUnlink(doc)">
              </button>
            </td>
          </tr>
        </ng-template>
      </p-table>
    </div>

    <!-- SECTION 2: DOCUMENTOS AUXILIARES -->
    <div class="surface-card border-round-xl p-4 shadow-1 border-1 border-200">
      <div class="flex justify-content-between align-items-center mb-3">
        <div class="flex align-items-center gap-2">
          <div class="badge-icon-circle bg-orange-50 text-orange-600">
            <i class="pi pi-cog"></i>
          </div>
          <div>
            <h3 class="text-lg font-bold text-900 m-0">Documentos Auxiliares</h3>
            <small class="text-500">Instructivos de equipos, preparación o guías complementarias.</small>
          </div>
        </div>
        <p-button
          label="+ Agregar Auxiliar"
          [text]="true"
          size="small"
          severity="warn"
          (onClick)="openLinkDialog('AUXILIAR')">
        </p-button>
      </div>

      <div *ngIf="!auxiliaryDocuments().length" class="empty-state-box p-4 border-round-lg border-1 border-dashed border-300 text-center">
        <i class="pi pi-wrench text-3xl text-400 mb-2"></i>
        <p class="text-600 font-medium m-0">No hay documentos auxiliares vinculados</p>
        <small class="text-500">Haga clic en "+ Agregar Auxiliar" para asociar un documento auxiliar.</small>
      </div>

      <p-table
        *ngIf="auxiliaryDocuments().length"
        [value]="auxiliaryDocuments()"
        styleClass="p-datatable-sm p-datatable-striped"
        responsiveLayout="scroll">
        <ng-template pTemplate="header">
          <tr>
            <th style="width: 15%">Código</th>
            <th style="width: 25%">Nombre Documento</th>
            <th style="width: 15%">Tipo</th>
            <th style="width: 15%">Ámbito</th>
            <th style="width: 12%">Versión Vigente</th>
            <th style="width: 10%">Notas</th>
            <th style="width: 8%" class="text-center">Acciones</th>
          </tr>
        </ng-template>
        <ng-template pTemplate="body" let-doc>
          <tr>
            <td>
              <span class="font-bold text-orange-700">{{ doc.qualityDocument?.code || '—' }}</span>
            </td>
            <td>
              <span class="font-semibold text-800">{{ doc.qualityDocument?.name }}</span>
            </td>
            <td>
              <p-tag [value]="doc.qualityDocument?.docType?.value || 'Instructivo'" severity="warning"></p-tag>
            </td>
            <td>
              <span class="text-sm text-700">
                <i class="pi" [ngClass]="doc.assayAnaliteId ? 'pi-filter text-indigo-500' : 'pi-building text-blue-500'"></i>
                {{ getScopeLabel(doc) }}
              </span>
            </td>
            <td>
              <ng-container *ngIf="getValidVersion(doc.qualityDocument) as validVer">
                <div class="flex align-items-center gap-2">
                  <p-tag [value]="'v' + validVer.versionNumber" severity="success"></p-tag>
                  <button
                    *ngIf="validVer.file"
                    pButton
                    type="button"
                    icon="pi pi-download"
                    class="p-button-rounded p-button-text p-button-sm text-primary"
                    pTooltip="Descargar versión vigente (PDF)"
                    tooltipPosition="top"
                    (click)="downloadDocumentFile(doc.qualityDocument)">
                  </button>
                </div>
              </ng-container>
              <span *ngIf="!getValidVersion(doc.qualityDocument)" class="text-xs text-400">Sin versión vigente</span>
            </td>
            <td>
              <span class="text-sm text-600 font-italic" [pTooltip]="doc.notes" tooltipPosition="top">
                {{ doc.notes ? (doc.notes | slice:0:30) + (doc.notes.length > 30 ? '...' : '') : '—' }}
              </span>
            </td>
            <td class="text-center">
              <button
                pButton
                type="button"
                icon="pi pi-trash"
                class="p-button-rounded p-button-text p-button-danger p-button-sm"
                pTooltip="Desvincular documento"
                tooltipPosition="top"
                (click)="confirmUnlink(doc)">
              </button>
            </td>
          </tr>
        </ng-template>
      </p-table>
    </div>

  </ng-container>

  <!-- DIALOG: ASOCIAR DOCUMENTO -->
  <p-dialog
    header="Asociar Documento de Calidad"
    [(visible)]="showLinkModal"
    [modal]="true"
    [style]="{ width: '540px' }"
    [draggable]="false"
    [resizable]="false"
    appendTo="body">

    <form [formGroup]="linkForm" (ngSubmit)="saveLink()" class="flex flex-column gap-4 pt-2">

      <!-- Ámbito de Asignación -->
      <div class="flex flex-column gap-2">
        <label class="font-semibold text-sm">Vincular a:</label>
        <p-select
          formControlName="targetScope"
          [options]="scopeOptions() | slice:1"
          optionLabel="label"
          optionValue="value"
          appendTo="body"
          class="w-full">
        </p-select>
        <small class="text-500">
          Puede vincular el documento a todo el ensayo o a una determinación puntual.
        </small>
      </div>

      <!-- Selector de Rol -->
      <div class="flex flex-column gap-2">
        <label class="font-semibold text-sm">Rol del Documento *</label>
        <p-select
          formControlName="role"
          [options]="roleOptions"
          optionLabel="label"
          optionValue="value"
          appendTo="body"
          class="w-full">
        </p-select>
      </div>

      <!-- Selector de Documento de Calidad -->
      <div class="flex flex-column gap-2">
        <label class="font-semibold text-sm">Documento de Calidad (Vigente) *</label>
        <p-select
          formControlName="qualityDocumentId"
          [options]="availableQualityDocs()"
          optionLabel="name"
          optionValue="id"
          [filter]="true"
          filterBy="code,name,docType.value"
          placeholder="Seleccionar documento..."
          appendTo="body"
          class="w-full">
          <ng-template let-doc pTemplate="item">
            <div class="flex flex-column">
              <span class="font-semibold">{{ doc.code }} — {{ doc.name }}</span>
              <small class="text-500">{{ doc.docType?.value || 'Doc' }} | Sector: {{ doc.sector?.value || '—' }}</small>
            </div>
          </ng-template>
        </p-select>
        <small *ngIf="linkForm.get('qualityDocumentId')?.invalid && linkForm.get('qualityDocumentId')?.touched" class="p-error">
          Debe seleccionar un documento de calidad.
        </small>
      </div>

      <!-- Notas / Observaciones -->
      <div class="flex flex-column gap-2">
        <label class="font-semibold text-sm">Notas / Observaciones (Opcional)</label>
        <textarea
          pInputTextarea
          formControlName="notes"
          rows="3"
          placeholder="Observaciones o indicaciones adicionales sobre el documento..."
          class="w-full">
        </textarea>
      </div>

      <!-- Footer Buttons -->
      <div class="flex justify-content-end gap-2 mt-3 pt-3 border-top-1 border-200">
        <p-button
          label="Cancelar"
          icon="pi pi-times"
          [text]="true"
          severity="secondary"
          (onClick)="showLinkModal = false">
        </p-button>
        <p-button
          label="Vincular Documento"
          icon="pi pi-check"
          type="submit"
          [disabled]="linkForm.invalid"
          [loading]="isSaving()">
        </p-button>
      </div>

    </form>
  </p-dialog>

</div>
